import { Injectable } from '@angular/core';

import { DataService } from '../network/data.service';
import { ProjectListItem } from './project-list-item';
import { CardsModel } from './cards/cards-model';
import { DriverTypeFilter } from './driver-types/driver-type-filter';
import { DimensionsFilter } from './dimensions-filtering/dimensions-filter';
import { ConfidenceModel } from './cards/confidence-model';
import { DriverType } from './driver-types/driver-type';
import { DriverSelectionAlgorithm } from './cards/driver-selection-algorithm';
import { DimensionLockType } from './cards/dimension-lock-type';
import { BenchmarksModel } from '../benchmarks/benchmarks-model';
import { ViewsModel } from './cards/views-model';

@Injectable()
export class ProjectService {
    projectId: string;

    constructor(private dataService: DataService) {
    }

    public setProjectId(projectId: string): void {
        this.projectId = projectId;
    }

    public getProjectId(): string {
        if (!this.projectId) {
            console.error('projectId is empty');
        }
        return this.projectId;
    }

    getDataSanityCheckResult(projectId: string) {
        this.setProjectId(projectId);
        return this.dataService.get('import/datasanity/' + projectId);
    }

    async getOriginalFileName(projectId: string) {
        this.setProjectId(projectId);
        let res = await this.dataService.get<any>('upload/getOriginalFileName/' + projectId);
        return res.originalFilename;
    }

    public getProjectsList() : Promise<ProjectListItem[]> {
        return this.dataService.get<ProjectListItem[]>('projects');
    }

    public deleteProject(projectId: string) {
        return this.dataService.post('projects/delete/' + projectId, {});
    }

    public duplicateProject(projectId: string) {
        return this.dataService.post('projects/duplicate/' + projectId, {});
    }

    public getViews(projectId: string): Promise<ViewsModel> {
        this.setProjectId(projectId);
        let timeZoneOffset = -new Date().getTimezoneOffset();
        return this.dataService.get<ViewsModel>(`processing/getViews?projectId=${projectId}&tz=${timeZoneOffset}`);
    }

    public reorderCards(viewId: string, tabId: string, prevIndex: number, curIndex: number) {
        return this.dataService.post<CardsModel>('processing/reorderCards', {
            viewId: viewId,
            tabId: tabId,
            prevIndex: prevIndex,
            curIndex: curIndex
        });
    }

    public setCardLock(viewId: string, tabId: string, cardIndex: number, isLocked: boolean) {
        return this.dataService.post('processing/setCardLock', {
            viewId: viewId,
            tabId: tabId,
            cardIndex: cardIndex,
            isLocked: isLocked
        });
    }

    public renameProject(projectId: string, newName: string) {
        return this.dataService.post('projects/rename/' + projectId, {
            newName: newName,
        });
    }

    public undo(viewId: string): Promise<CardsModel> {
        return this.dataService.post<CardsModel>('processing/undo', {
            viewId: viewId,
        });
    }

    public redo(viewId: string): Promise<CardsModel> {
        return this.dataService.post<CardsModel>('processing/redo', {
            viewId: viewId,
        });
    }

    public applyDriverTypeFilter(viewId: string, tabId: string, filter: DriverTypeFilter) {
        return this.dataService.post<CardsModel>('processing/applyDriverTypeFilters', {
            viewId: viewId,
            tabId: tabId,
            typeFilter: filter,
        });
    }

    public applyDimensionsFilter(viewId: string, tabId: string, filter: DimensionsFilter) {
        return this.dataService.post<CardsModel>('processing/applyDimensionsFilter', {
            viewId: viewId,
            tabId: tabId,
            dimensionsFilter: filter,
        });
    }

    public applyDepthFilters(viewId: string, tabId: string, minDriverDepth: number, maxDriverDepth: number, dimLocks: DimensionLockType[]) {
        return this.dataService.post<CardsModel>('processing/applyDriverDepthFilters', {
            viewId: viewId,
            tabId: tabId,
            minDriverDepth: minDriverDepth,
            maxDriverDepth: maxDriverDepth,
            dimensionLocks: dimLocks,
        });
    }
    
    public getConfidence(viewId: string, tabId: string, cardIndex: number, cardsCount: number, algorithm: DriverSelectionAlgorithm) {
        return this.dataService.get<ConfidenceModel>(`processing/confidence?viewId=${viewId}&tabId=${tabId}&cardIndex=${cardIndex}&cardsCount=${cardsCount}&algorithm=${algorithm}`);
    }
    
    public replaceCard(viewId: string, tabId: string, cardIndex: number, dimensions: string[], driverType : DriverType, formulaComponentIndex: number) {
        return this.dataService.post<any>('processing/replaceCard', {
            viewId: viewId,
            tabId: tabId,
            cardIndex: cardIndex,
            dimensions: dimensions,
            driverType: driverType, 
            formulaComponentIndex: formulaComponentIndex,
        });
    }

    public addCardBeforeConfidenceCheckedCard(viewId: string, tabId: string, cardIndex: number, dimensions: string[], driverType : DriverType, formulaComponentIndex: number) {
        return this.dataService.post<any>('processing/addCardBeforeConfidenceCheckedCard', {
            viewId: viewId,
            tabId: tabId,
            cardIndex: cardIndex,
            dimensions: dimensions,
            driverType: driverType, 
            formulaComponentIndex: formulaComponentIndex,
        });
    }

    public abortProcessing(projectId: string) {
        return this.dataService.post<any>(`processing/abort?projectId=${projectId}`, {});
    }
}
